<script>
import {defineComponent} from 'vue';
import {SVG} from './svg_utils.ts';

// A component for an image that is generated on the server, and fetching it may
// need to be retried.  This component attempts to present an interface of a
// regular image element, while handling retrying under the hood.
//
// Events:
// - load: when the image loads successfully
// - error: when fetching is aborted and no more retries will be attempted
export default defineComponent({
  name: 'GeneratedImage',

  inject: ['container'],

  props: {
    // The URL of the image.
    src: {
      type: String,
      default: '',
    },
    prefix: {
      type: String,
      default: '',
    },
    // Transform the image before displaying it.  Function should return a
    // promise that resolves to the new image blob.
    imageTransform: {
      type: Function,
      default: (blob) => Promise.resolve(blob),
    },
    // The classes of the <img> element.
    imageClass: {
      type: [String, Array, Object],
      default: '',
    },
    // Alt text for accessibility.
    alt: {
      type: String,
      required: false,
      default: '',
    },
    // Element ID for the value of aria-describedby attribute of the img.
    describedBy: {
      type: String,
      default: '',
    },
    // The Snippet object used to calculate the coordinates and dimensions.
    snippet: {
      type: Object,
      required: true,
    },
    isFlipped: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['error', 'load'],

  data() {
    return {
      dataUrl: null,
      viewBox: null,
    };
  },

  computed: {
    style() {
      return this.snippet.style();
    },

    fullPrefix() {
      return [
        'snippet', // base prefix
        this.prefix, // custom prefix for this image
        (this.isFlipped ? 'back' : 'front'), // flipped images aren't the same even though they share a URL
      ].filter((str) => str.length > 0).join(':');
    },

    fragment() {
      if (!this.viewBox) {
        return '#';
      }

      const box = this.viewBox;
      const x = box.x + (this.snippet.left || 0) * box.width;
      const y = box.y + (this.snippet.top || 0) * box.height;
      const width = ((this.snippet.right || 1) - (this.snippet.left || 0)) * box.width;
      const height = ((this.snippet.bottom || 1) - (this.snippet.top || 0)) * box.height;
      return `#svgView(viewBox(${x.toFixed(2)},${y.toFixed(2)},${width.toFixed(2)},${height.toFixed(2)});preserveAspectRatio(none))`;
    },
  },

  watch: {
    src() {
      this.release();
      this.fetch();
    },
    prefix() {
      this.release();
      this.fetch();
    },
  },

  created() {
    this.fetch();
  },

  unmounted() {
    this.release();
  },

  methods: {
    async fetch() {
      const src = this.src;
      const prefix = this.fullPrefix;

      const opts = {
        prefix,
        transform: this.imageTransform,
      };

      try {
        const svg = await SVG.fetch(this.container, src, opts);
        if (this.fullPrefix === prefix && this.src === src) {
          this.svg = svg;
          this.viewBox = svg.viewBox;
          this.dataUrl = svg.url();
        }
      } catch (e) {
        this._onError(e);
      }
    },

    handleLoad() {
      requestAnimationFrame(() => {
        this.$emit('load');
      });
    },

    // Call this when there is some non-fetching-related error.
    _onError(error) {
      console.error(error);
      this.$emit('error', error);
    },

    // cleanup any allocated resources (e.g. object URLs)
    release() {
      if (this.svg) {
        this.svg.release();
      }
      this.svg = null;
      this.dataURL = null;
    },
  },
});

</script>

<template>
  <img
    v-if="dataUrl"
    ref="imgElement"
    :src="dataUrl + fragment"
    :class="[
      imageClass,
      { 'flipped': isFlipped },
    ]"
    :alt="alt"
    :style="style"
    :aria-describedby="describedBy"
    @load="handleLoad"
  >
</template>

<style scoped>
img.flipped {
  transform: scale(-1, 1);
}
</style>
